import type { AgentFrontend } from '@septima/contract';
import { useMemo } from 'react';
import { Bot } from 'lucide-react';
import { useAgent } from '@/contexts/AgentContext';
import { useReviewQueueCounts } from '@/lib/use-review-queue-counts';
import { useAgentAlerts } from '@/lib/use-agent-alerts';
import { cn } from '@/lib/utils';
import { useAgentPanel } from './AgentPanel';

interface AgentBadgeProps {
    frontend: AgentFrontend;
}

export default function AgentBadge({ frontend }: AgentBadgeProps) {
    const { tasks } = useAgent();
    const { openPanel } = useAgentPanel();
    // Pending review count is the unified figure — tasks + persisted
    // artefacts. Backed by the lightweight counts endpoint so the badge does
    // not assemble the full queue model in chrome. The drawer is a peek; the
    // full review surface is the Agents Kanban "Needs Review" column
    // (/{frontend}/agents).
    const { itemCount: reviewCount } = useReviewQueueCounts(frontend);

    const visible = useMemo(
        () => tasks.filter((t) => t.frontend === frontend || t.frontend === 'global'),
        [tasks, frontend],
    );
    // "Active" covers both `queued` and `running`. The AgentRuntime path
    // inserts as `queued`, then the server flips to `running` — if we only
    // count `running`, the badge stays at 0 during the brief queued window
    // (and through any client/server cache lag).
    const activeCount = visible.filter(
        (t) => t.status === 'running' || t.status === 'queued',
    ).length;

    // Failed tasks surface as a red "N failed" state until the user opens the
    // panel to check them (which acknowledges them and returns the badge to
    // idle). A brand-new failure re-alerts. See use-agent-alerts.
    const failedIds = useMemo(
        () => visible.filter((t) => t.status === 'failed').map((t) => t.id),
        [visible],
    );
    const { unseenFailedCount, acknowledge } = useAgentAlerts(failedIds);

    const total = activeCount + reviewCount + unseenFailedCount;
    const hasFailures = unseenFailedCount > 0;

    return (
        <button
            type="button"
            data-testid="agent-badge"
            // Opening the panel is the "check the problems" gesture — acknowledge
            // surfaced failures so the badge clears once they have been seen.
            onClick={() => {
                if (hasFailures) acknowledge();
                openPanel({ frontend });
            }}
            aria-label={`Open agent panel (${activeCount} running, ${reviewCount} review, ${unseenFailedCount} failed)`}
            className={cn(
                'w-full flex items-center gap-2 px-2.5 py-2 rounded-[10px] text-annotation',
                'border bg-[rgb(var(--color-surface))]',
                'hover:bg-[rgb(var(--color-surface-secondary))] transition-colors',
                'text-[rgb(var(--color-text-primary))]',
                hasFailures
                    ? 'border-[rgb(var(--color-rag-red)/0.4)]'
                    : 'border-[rgb(var(--color-divider))]',
            )}
        >
            <span className="relative flex items-center justify-center w-5 h-5 flex-shrink-0">
                <Bot
                    className={cn(
                        'w-4 h-4',
                        hasFailures
                            ? 'text-[rgb(var(--color-rag-red))]'
                            : 'text-[rgb(var(--sidebar-accent))]',
                        activeCount > 0 && 'animate-pulse',
                    )}
                />
                {(activeCount > 0 || hasFailures) && (
                    <span
                        className={cn(
                            'absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full',
                            hasFailures
                                ? 'bg-[rgb(var(--color-rag-red))]'
                                : 'bg-[rgb(var(--color-rag-green))] animate-pulse',
                        )}
                    />
                )}
            </span>
            <span className="flex-1 text-left truncate">Agent Activity</span>
            {total > 0 ? (
                <span className="flex items-center gap-1.5 text-annotation">
                    {activeCount > 0 && (
                        <span className="text-[rgb(var(--color-rag-green))]">
                            {activeCount} Active
                        </span>
                    )}
                    {reviewCount > 0 && (
                        <span className="text-[rgb(var(--color-rag-amber))]">
                            {reviewCount} Review
                        </span>
                    )}
                    {unseenFailedCount > 0 && (
                        <span className="text-[rgb(var(--color-rag-red))]">
                            {unseenFailedCount} Failed
                        </span>
                    )}
                </span>
            ) : (
                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">Idle</span>
            )}
        </button>
    );
}
